"use client";

import { X, AlertCircle, CheckCircle, Info, AlertTriangle } from "lucide-react";
import { useEffect } from "react";

export type AlertType = "info" | "success" | "warning" | "error" | "confirm";

interface AlertModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  title?: string;
  message: string;
  type?: AlertType;
  confirmText?: string;
  cancelText?: string;
}

export function AlertModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  type = "info",
  confirmText = "OK",
  cancelText = "Cancel",
}: AlertModalProps) {
  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isConfirm = type === "confirm";

  // Icon and color based on type
  const getIconAndColor = () => {
    switch (type) {
      case "success":
        return {
          Icon: CheckCircle,
          color: "#10b981",
          bgColor: "bg-green-50 dark:bg-green-900/20",
          iconColor: "text-green-500",
        };
      case "error":
        return {
          Icon: AlertCircle,
          color: "#ef4444",
          bgColor: "bg-red-50 dark:bg-red-900/20",
          iconColor: "text-red-500",
        };
      case "warning":
        return {
          Icon: AlertTriangle,
          color: "#f59e0b",
          bgColor: "bg-yellow-50 dark:bg-yellow-900/20",
          iconColor: "text-yellow-500",
        };
      case "confirm":
        return {
          Icon: AlertCircle,
          color: "#3b82f6",
          bgColor: "bg-blue-50 dark:bg-blue-900/20",
          iconColor: "text-blue-500",
        };
      default:
        return {
          Icon: Info,
          color: "#3b82f6",
          bgColor: "bg-blue-50 dark:bg-blue-900/20",
          iconColor: "text-blue-500",
        };
    }
  };

  const { Icon, bgColor, iconColor } = getIconAndColor();

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={handleBackgroundClick}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative bg-white dark:bg-plantista-900 rounded-xl shadow-2xl max-w-md w-full border border-gray-200 dark:border-plantista-800 animate-in fade-in zoom-in duration-200">
        {/* Header */}
        <div className="flex items-start justify-between p-6 pb-4">
          <div className="flex items-start space-x-3 flex-1">
            <div className={`${bgColor} p-2 rounded-full flex-shrink-0`}>
              <Icon className={`w-6 h-6 ${iconColor}`} />
            </div>
            <div className="flex-1 min-w-0">
              {title && (
                <h3 className="text-lg font-bold text-plantista-950 dark:text-white mb-2">
                  {title}
                </h3>
              )}
              <p className="text-plantista-700 dark:text-plantista-300 text-sm leading-relaxed whitespace-pre-wrap">
                {message}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-plantista-800 rounded-full transition-colors flex-shrink-0 ml-2"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-plantista-400" />
          </button>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 p-6 pt-2">
          {isConfirm && (
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-gray-700 dark:text-plantista-300 hover:bg-gray-100 dark:hover:bg-plantista-800 rounded-lg transition-all"
            >
              {cancelText}
            </button>
          )}
          <button
            onClick={handleConfirm}
            className="px-4 py-2 text-sm font-semibold text-white dark:text-plantista-950 rounded-lg transition-all hover:opacity-90"
            style={{ backgroundColor: "#10b981" }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
